import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Package, Calendar, MapPin, CheckCircle, XCircle, Clock, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { driverService } from '../services/driverService';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';

interface HistoryItem {
  id: string;
  shipmentId: string;
  date: string;
  assignedAt: string;
  origin: string;
  destination: string;
  weight: string;
  amount: number;
  status: 'completed' | 'cancelled' | 'in-progress';
}

export function DriverMobileHistory() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'completed' | 'cancelled'>('all');
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = getStoredDriverMobileUser<any>();
    if (!storedUser) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    try {
      setUser(storedUser);
      loadHistory(storedUser.id);
    } catch (error) {
      console.error('Error loading user data:', error);
      navigate('/driver-mobile-auth', { replace: true });
    }
  }, [navigate]);

  const loadHistory = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);
      const driverResponse: any = await driverService.getDriverByUserId(userId);
      const driver = driverResponse?.data || driverResponse || {};
      if (!driver?.id) {
        setHistory([]);
        return;
      }

      const shipmentsResponse: any = await shipmentServiceReal.getShipments({ driverId: String(driver.id), assignmentResponseStatus: 'accepted' });
      const rows = Array.isArray(shipmentsResponse?.data) ? shipmentsResponse.data : [];
      const mapped: HistoryItem[] = rows
        .filter((item: any) => {
          const processStatus = String(item.process_status || '').trim();
          const shipmentStatus = String(item.status || '').trim().toLowerCase();
          return processStatus === 'bosaltma-tamamlandi' || shipmentStatus === 'delivered' || shipmentStatus === 'cancelled';
        })
        .map((item: any) => ({
          id: String(item.id),
          shipmentId: String(item.shipment_no || item.id),
          date: new Date(item.delivery_date || item.updated_at || item.created_at || Date.now()).toLocaleDateString('tr-TR'),
          assignedAt: item.assigned_at ? new Date(item.assigned_at).toLocaleString('tr-TR') : '-',
          origin: [item.pickup_name, item.pickup_city, item.pickup_district].filter(Boolean).join(' / ') || 'Yükleme',
          destination: [item.delivery_name, item.delivery_city, item.delivery_district].filter(Boolean).join(' / ') || 'Teslimat',
          weight: String(item.weight_kg || item.cargo_weight || 0),
          amount: Number(item.final_price || item.base_price || 0),
          status: String(item.status || '').trim().toLowerCase() === 'cancelled' ? 'cancelled' : 'completed',
        }));
      setHistory(mapped);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('Geçmiş sevkiyatlar yüklenemedi');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: history.length,
    completed: history.filter(h => h.status === 'completed').length,
    cancelled: history.filter(h => h.status === 'cancelled').length,
    inProgress: history.filter(h => h.status === 'in-progress').length,
  };

  const filteredHistory = filter === 'all' 
    ? history 
    : history.filter(h => h.status === filter);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'in-progress':
        return <Clock className="w-5 h-5 text-orange-600" />;
      default:
        return <Package className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Tamamlandı';
      case 'cancelled':
        return 'İptal Edildi';
      case 'in-progress':
        return 'Devam Ediyor';
      default:
        return 'Bilinmiyor';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'in-progress':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (loading) return <div className="safe-area-page min-h-dvh bg-gray-50 flex items-center justify-center"><div className="flex flex-col items-center gap-3"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /><span>Yükleniyor...</span></div></div>;

  if (!user) return null;

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-orange-600 dark:to-orange-700 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Taşıma Geçmişi</h1>
          <div className="w-9"></div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-orange-100">Toplam</p>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-orange-100">Tamamlanan</p>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.completed}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-orange-100">Devam Eden</p>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.inProgress}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-orange-100">İptal</p>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.cancelled}</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Tümü
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              filter === 'completed'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Tamamlanan
          </button>
          <button
            onClick={() => setFilter('cancelled')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              filter === 'cancelled'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            İptal
          </button>
        </div>
      </div>

      {/* History List */}
      <div className="px-4 pt-4 space-y-3">
        {error && (
          <div className="rounded-xl bg-white p-6 text-center shadow-sm">
            <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
            <p className="text-sm text-gray-700">{error}</p>
          </div>
        )}
        {filteredHistory.map((item) => (
          <div
            key={item.id}
            onClick={() => item.status === 'completed' && navigate(`/driver-mobile-shipment-tracking/${item.id}`)}
            className={`bg-white rounded-xl shadow-sm overflow-hidden border-l-4 ${
              item.status === 'completed' ? 'border-green-500' :
              item.status === 'cancelled' ? 'border-red-500' :
              'border-orange-500'
            } ${item.status === 'completed' ? 'cursor-pointer hover:bg-slate-50' : ''}`}
          >
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-900 font-bold px-3 py-1 rounded text-sm">
                    {item.shipmentId}
                  </span>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getStatusColor(item.status)}`}>
                    {getStatusIcon(item.status)}
                    <span>{getStatusText(item.status)}</span>
                  </div>
                </div>
              </div>

              {/* Route */}
              <div className="space-y-2 mb-3">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Yükleme</p>
                    <p className="text-sm font-medium text-gray-900">{item.origin}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Teslimat</p>
                    <p className="text-sm font-medium text-gray-900">{item.destination}</p>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="flex items-center justify-between pt-3 border-t">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{item.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>Atama: {item.assignedAt}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    <span>{item.weight} ton</span>
                  </div>
                </div>
                {item.status === 'completed' && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Kazanç</p>
                    <p className="text-sm font-bold text-green-600">
                      {item.amount.toLocaleString('tr-TR')} ₺
                    </p>
                  </div>
                )}
              </div>
              {item.status === 'completed' ? <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-blue-600">Sevkiyati Ac <ChevronRight className="h-4 w-4" /></div> : null}
            </div>
          </div>
        ))}

        {filteredHistory.length === 0 && (
          <div className="bg-white rounded-xl p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Bu kategoride taşıma bulunmuyor</p>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
